<script lang="ts">
  /**
   * A value the customer has not bought, blurred in the middle.
   *
   * # `masked` is the whole of the decision
   *
   * The blur is a picture of what the API did: it sends a scrambled name, and
   * this makes the scrambling legible as "not yours yet" rather than as a
   * company with a strange name. When the API sends the real thing, blurring it
   * is worse than useless — it hides data the customer is entitled to and makes
   * signing in look broken, which is exactly what it did.
   *
   * This component cannot tell the two apart by looking: a masked name is a
   * plausible string. So the caller says, and the default is `true` so that
   * every existing use, and every signed-out visitor, is unchanged.
   */
  export let name = '';
  export let opacity = 0.85; // Default opacity, can be overridden
  export let masked = true;

  function getBlurParts(str: string) {
    if (!str) return { start: '', middle: '', end: '' };

    const len = str.length;
    let prefix = 0;
    let suffix = 0;

    if (len <= 4) {
      prefix = 1;
      suffix = 1;
    } else if (len <= 8) {
      prefix = 2;
      suffix = 2;
    } else if (len <= 12) {
      prefix = 3;
      suffix = 2;
    } else {
      prefix = 3;
      suffix = 3;
    }

    const start = str.slice(0, prefix);
    const middle = str.slice(prefix, len - suffix);
    const end = str.slice(len - suffix);

    return { start, middle, end };
  }

  $: parts = getBlurParts(name);
</script>

{#if masked}
  <span class="obfuscated-text" style="--blur-opacity: {opacity}">
    <span class="visible-part">{parts.start}</span>
    <span class="blurred-part">{parts.middle}</span>
    <span class="visible-part">{parts.end}</span>
  </span>
{:else}
  <!-- Bought, or never hidden. The same inline box, so nothing shifts when a
       row changes from one to the other. -->
  <span class="obfuscated-text">{name}</span>
{/if}

<style>
  .obfuscated-text {
    position: relative;
    display: inline-flex;
    align-items: baseline;
  }

  .visible-part {
    display: inline;
  }

  .blurred-part {
    display: inline;
    position: relative;
    filter: blur(4px);
    user-select: none;
    pointer-events: none;
    opacity: var(--blur-opacity, 0.85);
  }

  /* Add a subtle overlay effect */
  .blurred-part::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      rgba(229, 231, 235, 0.15) 0%,
      rgba(229, 231, 235, 0.25) 50%,
      rgba(229, 231, 235, 0.15) 100%
    );
    pointer-events: none;
  }
</style>
